// The jurisdiction-aware option set for the contributor-designation picker in the
// finance ledger's expanded contribution row. The raw `ReceiptNature` enum is
// federal-flavored (Individual / PAC / Party / Corporate / In-Kind / Federal
// Transfer); rendering it verbatim on an Alabama campaign is wrong — AL's FCPA
// portal has NO "Party" or "Federal Transfer" source (they'd file mislabeled) and
// DOES offer an explicit "Other" source (EDI code 05) that the federal set lacks.
//
// This helper is the ONE source of truth mapping the campaign's served compliance
// profile to the picker options so the component never re-derives the list from
// scattered signals. It reads the profile (not a bare ruleset code) because
// `Federal Transfer` is gated on the jurisdiction's DECLARED
// `offersFederalTransferReceipt` capability. (#711 — it was previously gated on
// `federalTransferLimit != null`, a field the serve path never populated, so the
// option never rendered anywhere; and that field could not have been the gate
// anyway, since AL declares a federal-transfer limit and federal does not.) A two-way AL/else
// split over a five-way jurisdiction domain used to leak `Federal Transfer` to every
// non-AL ruleset (VA/NC/LA), a jurisdiction-meaningless receipt that only hard-fails
// at the state builder, not at pick time. AL labels are AL-specific ("Other" —
// mirroring the exact portal dropdown); every other jurisdiction reuses the shared
// treasurer-first `contributionTypeLabel`.

import type { ComplianceProfileDto } from '../generated/dtos.gen';
import { ReceiptNature } from '../generated/enums.gen';
import { contributionTypeLabel } from './contributionTypeLabel';

export interface ContributionDesignationOption {
  value: ReceiptNature;
  label: string;
}

/**
 * The subset of the served profile this helper reads. Kept narrow so callers (and
 * tests) can pass a partial without constructing the whole DTO; the real
 * {@link ComplianceProfileDto} satisfies it structurally.
 */
export type ContributionDesignationProfile = Pick<
  ComplianceProfileDto,
  'stateCode' | 'offersFederalTransferReceipt'
>;

// The Alabama FCPA "Transaction type" dropdown. "Other" is the portal's explicit
// code-05 source. In-Kind is included because it files as its OWN AL contribution
// type (the XML builder maps `ReceiptNature.InKind` → `cbContributionType` 04/05,
// distinct from Cash's 01/02) — so a treasurer must be able to designate it here;
// selecting it reveals the in-kind nature/description capture the AL filing requires.
// "Returned (Cash Only)" is the refund flag (`cbReturn`), not a source, so it stays
// absent, as does "Federal Transfer" (no AL source). ADR-057/#344 — the
// person/organization sources collapsed into the identity-less `Cash` receipt (the
// contributor's person-vs-org identity lives on the contact's ContactKind/OrganizationType).
const ALABAMA_DESIGNATION_OPTIONS: ContributionDesignationOption[] = [
  { value: ReceiptNature.Cash, label: contributionTypeLabel(ReceiptNature.Cash) },
  { value: ReceiptNature.InKind, label: contributionTypeLabel(ReceiptNature.InKind) },
  { value: ReceiptNature.Other, label: 'Other' },
];

/**
 * The contributor-designation options for a campaign, resolved from its served
 * compliance profile. Alabama (`stateCode === 'AL'`) returns the portal-mirrored
 * list. Every other jurisdiction gets the jurisdiction-neutral Cash/In-Kind pair,
 * plus `Federal Transfer` ONLY when the jurisdiction declares it
 * (`offersFederalTransferReceipt`) — so a loaded state profile (VA/NC/LA) never sees
 * the federal-only receipt. A missing profile (null) resolves to the safe neutral
 * pair rather than leaking the federal option while the request is in flight.
 */
export function contributionDesignationOptions(
  profile: ContributionDesignationProfile | null | undefined,
): ContributionDesignationOption[] {
  if ((profile?.stateCode ?? '').toUpperCase() === 'AL') {
    return ALABAMA_DESIGNATION_OPTIONS;
  }

  const options: ContributionDesignationOption[] = [
    { value: ReceiptNature.Cash, label: contributionTypeLabel(ReceiptNature.Cash) },
    { value: ReceiptNature.InKind, label: contributionTypeLabel(ReceiptNature.InKind) },
  ];
  if (profile?.offersFederalTransferReceipt === true) {
    options.push({
      value: ReceiptNature.FederalTransfer,
      label: contributionTypeLabel(ReceiptNature.FederalTransfer),
    });
  }
  return options;
}
